import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  LoadingOutlined,
  PlusOutlined,
  UserAddOutlined,
  StopOutlined,
  SettingOutlined,
  SearchOutlined,
  FilterOutlined,
} from "@ant-design/icons";
import SearchAndFilter from "../../../../components/Common/SearchFilter";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Switch,
  Checkbox,
  Empty,
} from "antd";
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { api } from "../../../../Assets/api/api";
import { useTrustedNetwork } from "../../../../context/TrustedNetworkContext";
import "./styles.scss";
import { Loader, Dialog } from "@fluentui/react-northstar";
import ConfirmationDialog from "../Common/ConfirmationDialog";
import {
  Box,
  Popover,
  Tooltip,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import defaultSchoolLogo from "../../../../Assets/images/DefaultSchoolLogo.png";
import GroupNetworkConfigDialog from "./GroupNetworkConfig/GroupNetworkConfigDialog";
import { useTranslation } from "react-i18next";

const Groups = () => {
  const { t } = useTranslation();
  const translation = t("trustedNetwork").groups;
  const { user, isUpdatingGroupNetwork, updateGroupNetworkConfig } =
    useTrustedNetwork();

  // Add responsive detection
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md")); // 768px and below
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("sm")); // 480px and below

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [groups, setGroups] = useState([]);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({
    open: false,
    groupId: null,
    isLoading: false,
  });
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingSchools, setIsLoadingSchools] = useState(false);
  const [form] = Form.useForm();
  const [invitedSchools, setInvitedSchools] = useState(new Map());
  const [selectedGroupId, setSelectedGroupId] = useState(null);
  const [loadingInvites, setLoadingInvites] = useState(new Set());
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filterData, setFilterData] = useState({});
  const [openDropdownIndex, setOpenDropdownIndex] = useState(-1);
  const [searchText, setSearchText] = useState({});
  const [filters, setFilters] = useState([]);
  const [onlySchoolsWithBranches, setOnlySchoolsWithBranches] = useState(false);

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [config, setConfig] = useState({
    shareCollection: false,
    shareSpinWheel: false,
  });

  const [deleteInviteDialog, setDeleteInviteDialog] = useState({
    open: false,
    invitationId: null,
    isLoading: false,
  });

  const [inviteConfirmDialog, setInviteConfirmDialog] = useState({
    open: false,
    groupId: null,
    tenantId: null,
    schoolName: null,
    isLoading: false,
  });

  // Main page search and filter states (separate from invite modal)
  const [mainSearchQuery, setMainSearchQuery] = useState("");
  const [filteredGroups, setFilteredGroups] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState({
    visibility: [],
    names: [],
  });

  const getInviteKey = (groupId, tenantId) => `${groupId}-${tenantId}`;

  const getInviteButtonText = (groupId, tenantId) => {
    const key = getInviteKey(groupId, tenantId);
    if (loadingInvites.has(key)) return "Inviting...";
    const invitation = invitedSchools.get(key);
    if (!invitation) return "Invite";

    switch (invitation.status) {
      case "pending":
        return "Invited";
      case "accepted":
        return "Accepted";
      case "rejected":
        return "Rejected";
      case "cancelled":
        return "Cancelled";
      default:
        return "Invite";
    }
  };

  const getInviteButtonIcon = (groupId, tenantId) => {
    const key = getInviteKey(groupId, tenantId);
    if (loadingInvites.has(key)) return <LoadingOutlined />;
    const invitation = invitedSchools.get(key);
    if (!invitation) return <UserAddOutlined />;

    switch (invitation.status) {
      case "pending":
        return <ClockCircleOutlined />;
      case "accepted":
        return <CheckCircleOutlined />;
      case "rejected":
        return <CloseCircleOutlined />;
      case "cancelled":
        return <StopOutlined />;
      default:
        return <UserAddOutlined />;
    }
  };

  const fetchInvitations = async (groupId) => {
    setIsLoadingInvitations(true);
    try {
      const response = await api.get(
        `/api/group-network/invitations/${groupId}${user.slug}`
      );

      if (response.status === 200) {
        const invitationMap = new Map();

        if (!response.data.result) {
          return;
        }

        const invitations = Array.isArray(response.data.result)
          ? response.data.result
          : [response.data.result];

        invitations.forEach((invitation) => {
          const key = getInviteKey(groupId, invitation.invitedSchoolId);
          invitationMap.set(key, {
            status: invitation.status,
            expiresAt: invitation.expiresAt,
            id: invitation.id,
          });
        });

        setInvitedSchools(invitationMap);
      }
    } catch (error) {
      console.error("Error fetching invitations:", error);
    } finally {
      setIsLoadingInvitations(false);
    }
  };

  // Helper to build query params for get-schools API
  const buildSchoolQueryParams = (params = {}) => {
    const query = {};
    Object.entries(params).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        // Join arrays as comma-separated strings for all filters except 'search' and 'slug'
        if (key === "search" || key === "slug") {
          query[key] = value;
        } else {
          query[key] = value.join(",");
        }
      } else if (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        !Array.isArray(value)
      ) {
        query[key] = value;
      }
    });
    return query;
  };

  // Helper to get just the slug value (not '?slug=...')
  const getSlugValue = (slug) => {
    if (typeof slug === "string" && slug.startsWith("?slug=")) {
      return slug.replace(/^\?slug=/, "");
    }
    return slug;
  };

  const fetchSchoolsWithFilters = async (groupId, filterParams = {}) => {
    try {
      setIsLoadingSchools(true);
      const params = {
        slug: getSlugValue(user.slug),
        ...buildSchoolQueryParams(filterParams),
      };

      // Add branch filtering parameter if enabled
      if (onlySchoolsWithBranches) {
        params.onlySchoolsWithBranches = "true";
      }

      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        searchParams.append(key, value);
      });
      const queryString = searchParams.toString();
      const url = `/api/group-network/get-schools/${groupId}?${queryString}`;

      const response = await api.get(url);
      if (response.status === 200) {
        setSchools(response.data.result);
        setFilteredSchools(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast.error(translation.errorFetchingSchools);
    } finally {
      setIsLoadingSchools(false);
    }
  };

  // Fetch filter options from backend for all filters
  const fetchFilterOptions = async () => {
    try {
      const response = await api.get(
        `/api/group-network/get-filter-options/${user.slug}`
      );
      if (response.status === 200 && response.data.result) {
        const result = response.data.result;
        // Dynamically build filters array from backend response
        const backendFilters = Object.entries(result)
          .filter(([, arr]) => Array.isArray(arr) && arr.length)
          .map(([key, arr]) => ({
            id: key,
            title: key.charAt(0).toUpperCase() + key.slice(1),
            label: arr,
            selected: 0,
          }));
        setFilters(backendFilters);
        // Reset searchText for new filters
        setSearchText(
          Object.fromEntries(backendFilters.map((f) => [f.id, ""]))
        );
      }
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  const handleConfigDialogOpen = (group) => {
    setSelectedGroup(group);
    setSelectedGroupId(group.id);
    // Get the current groups's configuration
    const currentConfig = {
      shareCollection: group.memberConfigs[0]?.shareCollection || false,
      shareSpinWheel: group.memberConfigs[0]?.shareSpinWheel || false,
    };
    setConfig(currentConfig);
    setConfigDialogOpen(true);
  };

  const handleConfigDialogClose = () => {
    // Prevent closing if update is in progress
    if (!isUpdatingGroupNetwork[selectedGroup?.id]) {
      setConfigDialogOpen(false);
      setSelectedGroup(null);
      setSelectedGroupId(null);
      setConfig({
        shareCollection: false,
        shareSpinWheel: false,
      });
    }
  };

  const handleConfigDialogConfirm = async () => {
    if (selectedGroup && selectedGroup.memberConfigs?.[0]) {
      await updateGroupNetworkConfig(
        selectedGroup.memberConfigs[0].groupNetworkId,
        config
      );

      setGroups((prevGroups) =>
        prevGroups.map((g) =>
          g.id === selectedGroup.id
            ? {
                ...g,
                memberConfigs: [
                  {
                    ...g.memberConfigs[0],
                    ...config,
                  },
                ],
              }
            : g
        )
      );
      handleConfigDialogClose();
    } else {
      toast.error("No group or configuration found.");
    }
  };

  const handleConfigChange = (key) => {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleOpenInviteDialog = (groupId) => {
    setInviteDialogOpen(true);
    setSelectedGroupId(groupId);
    fetchInvitations(groupId);
    fetchSchoolsWithFilters(groupId, {
      search: searchQuery,
      ...getSelectedFiltersObject(),
    });
    fetchFilterOptions(); // fetch filter options from backend
  };

  // Helper to build selected filters object dynamically
  const getSelectedFiltersObject = () => {
    const selectedFilters = {};
    filters.forEach((filter) => {
      selectedFilters[filter.id] = filterData[filter.id] || [];
    });
    return selectedFilters;
  };

  const fetchGroups = async () => {
    try {
      setIsFetching(true);
      const response = await api.get(`/api/group-network${user.slug}`);
      if (response.status === 200) {
        setGroups(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching groups:", error);
      toast.error("Error fetching groups. Please try again.");
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [user.slug]);

  useEffect(() => {
    setFilteredSchools(schools);
  }, [schools]);

  // Update filtered groups when groups or search query changes
  useEffect(() => {
    if (!groups) {
      setFilteredGroups([]);
      return;
    }

    let filtered = groups;

    // Apply search filter
    if (mainSearchQuery.trim()) {
      const query = mainSearchQuery.toLowerCase();
      filtered = filtered.filter(
        (group) =>
          group.name?.toLowerCase().includes(query) ||
          group.description?.toLowerCase().includes(query) ||
          group.creatorSchool?.name?.toLowerCase().includes(query)
      );
    }

    // Apply visibility filter
    if (selectedFilters.visibility && selectedFilters.visibility.length > 0) {
      filtered = filtered.filter((group) => {
        const visibility = group.isPublic ? "Public" : "Private";
        return selectedFilters.visibility.includes(visibility);
      });
    }

    // Apply names filter
    if (selectedFilters.names && selectedFilters.names.length > 0) {
      filtered = filtered.filter((group) =>
        selectedFilters.names.includes(group.name)
      );
    }

    setFilteredGroups(filtered);
  }, [groups, mainSearchQuery, selectedFilters]);

  // Filter options for the reusable component
  const filterOptions = [
    {
      id: "visibility",
      title: translation.visibility || "Visibility",
      label: ["Public", "Private"],
      search: false,
    },
    {
      id: "names",
      title: translation.groupNames || "Group Names",
      label: groups
        ? [...new Set(groups.map((g) => g.name).filter(Boolean))]
        : [],
      search: true,
    },
  ];

  // Filter handlers for the reusable component
  const handleFilterChange = (filterId, value, checked) => {
    setSelectedFilters((prev) => {
      const currentFilters = prev[filterId] || [];
      if (checked) {
        return {
          ...prev,
          [filterId]: [...currentFilters, value],
        };
      } else {
        return {
          ...prev,
          [filterId]: currentFilters.filter((item) => item !== value),
        };
      }
    });
  };

  const handleClearAllFilters = () => {
    setSelectedFilters({
      visibility: [],
      names: [],
    });
  };

  // Popover content for filter options
  const useStyles = makeStyles(() => ({
    arrow: {
      "&:before": {
        border: "1px solid #E6E8ED",
      },
    },
    tooltip: {
      minWidth: 266,
      backgroundColor: "white",
      color: "black",
      padding: "10px",
      fontSize: "16px",
      borderRadius: 12,
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    },
  }));

  const classes = useStyles();

  const handleDropdownToggle = (index) => {
    if (index !== openDropdownIndex) {
      const filterId = filters[index]?.id;
      if (filterId) {
        setSearchText((prev) => ({ ...prev, [filterId]: "" }));
      }
    }
    setOpenDropdownIndex(index === openDropdownIndex ? -1 : index);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenDropdownIndex(-1);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpenDropdownIndex(-1);
  };

  const toggle = (checked, val, filterId) => {
    setFilterData((prev) => {
      const newFilterData = { ...prev };
      if (!newFilterData[filterId]) {
        newFilterData[filterId] = [];
      }

      if (checked) {
        newFilterData[filterId] = [...newFilterData[filterId], val];
      } else {
        newFilterData[filterId] = newFilterData[filterId].filter(
          (v) => v !== val
        );
      }

      return newFilterData;
    });
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const showModal = () => {
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleDeleteGroup = async (groupId) => {
    setConfirmDeleteDialog((prev) => ({ ...prev, isLoading: true }));

    try {
      const response = await api.delete(
        `/api/group-network/${groupId}${user.slug}`
      );
      if (response.status === 200) {
        toast.success(translation.groupDeletedSuccess);
        fetchGroups();
      }
    } catch (error) {
      console.error("Error deleting group:", error);
      toast.error(
        error?.response?.data?.message || translation.errorDeletingGroup
      );
    } finally {
      // Always close dialog regardless of success or error
      setConfirmDeleteDialog({
        open: false,
        groupId: null,
        isLoading: false,
      });
    }
  };

  const handleSearch = async (value) => {
    setSearchQuery(value);
    await fetchSchoolsWithFilters(selectedGroupId, {
      search: value,
      ...getSelectedFiltersObject(),
    });
  };

  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const response = await api.post(`/api/group-network${user.slug}`, values);
      if (response.status === 201) {
        toast.success(translation.groupCreatedSuccess);
        handleCancel();
        fetchGroups(); // Refresh the groups list
      }
    } catch (error) {
      console.error("Error creating group:", error);
      toast.error(
        error?.response?.data?.message || translation.errorCreatingGroup
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteSchoolConfirm = (groupId, tenantId, schoolName) => {
    setInviteConfirmDialog({
      open: true,
      groupId,
      tenantId,
      schoolName,
      isLoading: false,
    });
  };

  const handleInviteSchool = async (groupId, tenantId) => {
    setInviteConfirmDialog((prev) => ({ ...prev, isLoading: true }));
    const key = getInviteKey(groupId, tenantId);
    setLoadingInvites((prev) => new Set([...prev, key]));
    try {
      const inviteData = {
        groupNetworkId: groupId,
        invitedSchoolId: tenantId,
        message: translation.inviteMessage,
      };

      const response = await api.post(
        `/api/group-network/invite${user.slug}`,
        inviteData
      );

      if (response.status === 201) {
        setInvitedSchools(
          (prev) =>
            new Map(
              prev.set(key, {
                status: "pending",
                expiresAt: response.data.result.expiresAt,
                id: response.data.result.id,
              })
            )
        );
        toast.success(translation.invitationSentSuccess);
      }
    } catch (error) {
      console.error("Error inviting school:", error);
      toast.error(
        error?.response?.data?.message || translation.errorSendingInvitation
      );
    } finally {
      setLoadingInvites((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setInviteConfirmDialog({
        open: false,
        groupId: null,
        tenantId: null,
        schoolName: null,
        isLoading: false,
      });
    }
  };

  const handleDeleteInvite = async () => {
    setDeleteInviteDialog((prev) => ({ ...prev, isLoading: true }));
    try {
      await api.delete(
        `/api/group-network/invitations/${deleteInviteDialog.invitationId}${user.slug}`
      );
      toast.success(translation.invitationDeletedSuccess);
      // Remove the invitation from the invitedSchools map immediately
      setInvitedSchools((prev) => {
        const updated = new Map(prev);
        for (let [key, value] of updated.entries()) {
          if (value.id === deleteInviteDialog.invitationId) {
            updated.delete(key);
            break;
          }
        }
        return updated;
      });
    } catch (error) {
      toast.error("Failed to delete invitation.");
    } finally {
      setDeleteInviteDialog({
        open: false,
        invitationId: null,
        isLoading: false,
      });
    }
  };

  const resetInviteDialog = () => {
    setInviteDialogOpen(false);
    setSearchQuery("");
    setFilteredSchools(schools);
    setFilterData({});
    setSearchText(Object.fromEntries(filters.map((f) => [f.id, ""])));
    setOnlySchoolsWithBranches(false);
  };

  const resetFilters = async () => {
    handleClose();
    setFilterData({});
    setSearchText(Object.fromEntries(filters.map((f) => [f.id, ""])));
    setOnlySchoolsWithBranches(false);
    await fetchSchoolsWithFilters(selectedGroupId, {});
  };

  const applyFilters = async () => {
    const selectedFilters = getSelectedFiltersObject();
    handleClose();
    setIsModalVisible(false);
    await fetchSchoolsWithFilters(selectedGroupId, {
      search: searchQuery,
      ...selectedFilters,
    });
  };

  // Main page search and filter handlers
  const handleMainSearch = (value) => {
    setMainSearchQuery(value);
  };

  const handleBranchFilterChange = async (checked) => {
    setOnlySchoolsWithBranches(checked);
    await fetchSchoolsWithFilters(selectedGroupId, {
      search: searchQuery,
      ...getSelectedFiltersObject(),
      onlySchoolsWithBranches: checked ? "true" : undefined,
    });
  };

  return (
    <>
      <div className="groups-container">
        <div
          className="w-100"
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <SearchAndFilter
            searchValue={mainSearchQuery}
            onSearchChange={handleMainSearch}
            searchPlaceholder={
              translation.searchGroupsPlaceholder || "Search groups..."
            }
            filterOptions={filterOptions}
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearAllFilters={handleClearAllFilters}
            filterButtonText={translation.filter || "Filter"}
            translations={{
              clear: translation.clear || "Clear",
              apply: translation.apply || "Apply",
              notFound: translation.notFound || "No options found",
            }}
          />
          <Button
            type="primary"
            onClick={showModal}
            className="create-group-btn"
            icon={<PlusOutlined />}
          >
            {translation.createNewGroup}
          </Button>
        </div>

        {isFetching ? (
          <div className="loading-container">
            <Loader />
          </div>
        ) : filteredGroups.length > 0 ? (
          <div className="groups-grid">
            {filteredGroups.map((group) => (
              <div key={group.id} className="group-card">
                <div className="group-card-header">
                  <div className="header-title">{group.name}</div>
                  <div className="header-actions">
                    {group.isPublic ? (
                      <span className="public-badge">{translation.public}</span>
                    ) : (
                      <span className="private-badge">
                        {translation.private}
                      </span>
                    )}
                    <Button
                      type="text"
                      icon={<UserAddOutlined />}
                      className="invite-btn"
                      onClick={() => handleOpenInviteDialog(group.id)}
                    />
                    <Button
                      type="text"
                      icon={<SettingOutlined />}
                      className="setting-btn"
                      onClick={() => handleConfigDialogOpen(group)}
                    />
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      className="delete-btn"
                      onClick={() =>
                        setConfirmDeleteDialog({
                          open: true,
                          groupId: group.id,
                          isLoading: false,
                        })
                      }
                    />
                  </div>
                </div>
                <Tooltip 
                  title={group.description && group.description.length > 100 ? group.description : ""} 
                  placement="top"
                  overlayStyle={{ maxWidth: '300px', wordWrap: 'break-word' }}
                >
                  <p className="group-description">
                    {group.description && group.description.length > 100
                      ? `${group.description.substring(0, 100)}...`
                      : group.description}
                  </p>
                </Tooltip>
                <div className="group-card-footer">
                  <span>
                    {translation.members}
                    {group.memberCount}
                  </span>
                  {group.creatorSchool?.name && (
                    <div className="creator-school">
                      {group.creatorSchool.logo && (
                        <img
                          src={
                            group.creatorSchool.logo +
                            process.env.REACT_APP_AZURE_SAS_TOKEN
                          }
                          alt="School logo"
                          className="school-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = defaultSchoolLogo;
                          }}
                        />
                      )}
                      <span>{group.creatorSchool.name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : groups.length > 0 ? (
          <div className="empty-state-container">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translation.noGroupsFoundWithFilters}
            />
          </div>
        ) : (
          <div className="empty-state-container">
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={translation.noGroupsFound || "No groups available"}
            />
          </div>
        )}

        <Modal
          title="Create New Group"
          open={isModalVisible}
          onCancel={handleCancel}
          footer={[
            <Button key="back" onClick={handleCancel}>
              {translation.cancel}
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={() => form.submit()}
              loading={isLoading}
            >
              {isLoading ? translation.creating : translation.createGroup}
            </Button>,
          ]}
          className="group-modal"
          width={600}
          centered
          confirmLoading={isLoading}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="group-form"
          >
            <Form.Item
              name="name"
              label={translation.groupName}
              rules={[
                { required: true, message: translation.groupNameRequired },
              ]}
            >
              <Input
                placeholder={translation.groupNamePlaceholder}
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item 
              name="description" 
              label={translation.description}
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) return Promise.resolve();
                    const wordCount = value.trim().split(/\s+/).length;
                    if (wordCount > 100) {
                       return Promise.reject(new Error(translation.descriptionWordLimitError));
                     }
                    return Promise.resolve();
                  }
                }
              ]}
            >
              <Input.TextArea
                placeholder={translation.descriptionPlaceholder}
                rows={4}
                disabled={isLoading}
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item
              name="maxMembers"
              label={translation.maximumMembers}
              initialValue={10}
              rules={[
                {
                  type: 'number',
                  min: 1,
                  max: 50,
                  message: translation.maxMembersRangeError
                }
              ]}
            >
              <InputNumber
                min={1}
                max={50}
                className="max-members-input"
                disabled={isLoading}
              />
            </Form.Item>

            <Form.Item
              name="isPublic"
              label={translation.publicGroup}
              valuePropName="checked"
              initialValue={false}
            >
              <Switch disabled={isLoading} />
            </Form.Item>
          </Form>
        </Modal>
      </div>

      <Dialog
        open={confirmDeleteDialog.open}
        onCancel={() =>
          !confirmDeleteDialog.isLoading &&
          setConfirmDeleteDialog({
            open: false,
            groupId: null,
            isLoading: false,
          })
        }
        header={translation.deleteGroup}
        content={
          confirmDeleteDialog.isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loader />
              <div style={{ marginTop: "10px" }}>Deleting group...</div>
            </div>
          ) : (
            translation.deleteGroupConfirm
          )
        }
        confirmButton={{
          content: translation.yesDelete,
          danger: true,
          disabled: confirmDeleteDialog.isLoading,
          onClick: () => handleDeleteGroup(confirmDeleteDialog.groupId),
        }}
        cancelButton={{
          content: translation.cancel,
          disabled: confirmDeleteDialog.isLoading,
        }}
      />

      <Modal
        title={
          <div className="invite-modal-header">
            <div className="invite-modal-title">
              <UserAddOutlined className="invite-icon" />
              <span>{translation.inviteSchoolsTitle}</span>
            </div>
            <div className="search-filter-wrapper">
              <Input
                placeholder={translation.searchSchoolsPlaceholder}
                className="school-search"
                size="large"
                allowClear
                onChange={(e) => handleSearch(e.target.value)}
                onSearch={handleSearch}
                value={searchQuery}
                prefix={<SearchOutlined className="search-icon" />}
              />

              <span
                className="filter-button"
                onClick={handleClick}
                style={{ cursor: "pointer" }}
              >
                <FilterOutlined />
                <span>{translation.filter}</span>
              </span>
            </div>
          </div>
        }
        open={inviteDialogOpen}
        onCancel={resetInviteDialog}
        closeIcon={<CloseOutlined className="close-icon" />}
        footer={[
          <Button
            key="back"
            onClick={resetInviteDialog}
            className="close-button"
            size="large"
          >
            {translation.close}
          </Button>,
        ]}
        width={1000}
        centered
        className="invite-schools-modal"
      >
        {isLoadingSchools || isLoadingInvitations ? (
          <div className="loading-container">
            <Loader size="medium" />
          </div>
        ) : (
          <div className="schools-container">
            {/* Branch Filtering Options */}
            <div
              style={{
                marginBottom: "20px",
                padding: "10px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
              }}
            >
              <Checkbox
                checked={onlySchoolsWithBranches}
                onChange={(e) => handleBranchFilterChange(e.target.checked)}
              >
                Only show schools with branches
              </Checkbox>
            </div>

            <div className="schools-grid">
              {filteredSchools.length === 0 ? (
                <div
                  className="no-schools-found-message"
                  style={{
                    width: "100%",
                    textAlign: "center",
                    padding: "40px 0",
                  }}
                >
                  <p>{translation.noSchoolsFound}</p>
                </div>
              ) : (
                filteredSchools
                  .map((school) => ({
                    ...school,
                    isInvited: invitedSchools.has(
                      getInviteKey(selectedGroupId, school.OwnStgUrl)
                    ),
                  }))
                  .map((school) => (
                    <div key={school.OwnStgUrl} className="school-card">
                      <div className="school-name">{school.School}</div>
                      <div className="school-logo">
                        {school.SchoolLogo ? (
                          <img
                            src={
                              school.SchoolLogo +
                              process.env.REACT_APP_AZURE_SAS_TOKEN
                            }
                            alt={school.School}
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.src = defaultSchoolLogo;
                            }}
                          />
                        ) : (
                          <div className="no-logo">
                            {school.School?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="school-stats">
                        <div className="stat-item">
                          <EnvironmentOutlined />
                          <span>
                            {school.SchoolAddress ||
                              translation.defaultSchoolAddress}
                          </span>
                        </div>
                        <div className="stat-item">
                          <GlobalOutlined />
                          <span>
                            {school.Country || translation.defaultCountry}
                          </span>
                        </div>
                      </div>
                      <div className="invite-action-footer">
                        {invitedSchools.get(
                          getInviteKey(selectedGroupId, school.OwnStgUrl)
                        )?.status === "pending" ? (
                          <>
                            <span className="invited-badge">
                              <ClockCircleOutlined
                                style={{ marginRight: 4, fontSize: 14 }}
                              />
                              Invited
                            </span>
                            <Tooltip
                              title="Withdraw Invitation"
                              placement="top"
                            >
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setDeleteInviteDialog({
                                    open: true,
                                    invitationId: invitedSchools.get(
                                      getInviteKey(
                                        selectedGroupId,
                                        school.OwnStgUrl
                                      )
                                    ).id,
                                    isLoading: false,
                                  })
                                }
                                disabled={deleteInviteDialog.isLoading}
                                style={{ marginLeft: 8 }}
                              >
                                <DeleteOutlined style={{ color: "#d73527" }} />
                              </IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            className={`status-btn ${
                              invitedSchools.get(
                                getInviteKey(selectedGroupId, school.OwnStgUrl)
                              )?.status || ""
                            }`}
                            icon={getInviteButtonIcon(
                              selectedGroupId,
                              school.OwnStgUrl
                            )}
                            onClick={() =>
                              !invitedSchools.has(
                                getInviteKey(selectedGroupId, school.OwnStgUrl)
                              ) &&
                              handleInviteSchoolConfirm(
                                selectedGroupId,
                                school.OwnStgUrl,
                                school.School
                              )
                            }
                            disabled={
                              invitedSchools.has(
                                getInviteKey(selectedGroupId, school.OwnStgUrl)
                              ) ||
                              loadingInvites?.has(
                                getInviteKey(selectedGroupId, school.OwnStgUrl)
                              )
                            }
                            loading={loadingInvites?.has(
                              getInviteKey(selectedGroupId, school.OwnStgUrl)
                            )}
                          >
                            {getInviteButtonText(
                              selectedGroupId,
                              school.OwnStgUrl
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        )}

        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          PaperProps={{
            style: {
              marginTop: "15px",
              minWidth: "200px",
              borderRadius: 12,
              boxShadow:
                "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
            },
          }}
        >
          {filters.map((data, index) => {
            const isOpenDropDown = index === openDropdownIndex;
            const filteredOptions = data.label.filter((o) => {
              return (
                (searchText[data.id] || "").length === 0 ||
                o
                  .toLowerCase()
                  .includes((searchText[data.id] || "").toLowerCase())
              );
            });
            const options =
              (searchText[data.id] || "").length > 0
                ? filteredOptions
                : data.label;
            return (
              <Box key={data.id}>
                {isSmallScreen ? (
                  // Collapsible accordion for small screens
                  <Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Karla",
                        width: "100%",
                        padding: "12px 16px",
                        borderRadius: "8px",
                        backgroundColor: isOpenDropDown
                          ? "#f5f5f5"
                          : "transparent",
                        "&:hover": {
                          cursor: "pointer",
                          backgroundColor: "#f0f0f0",
                        },
                      }}
                      onClick={() => handleDropdownToggle(index)}
                    >
                      <Typography
                        sx={{ flex: 1, fontFamily: "Karla", fontSize: "16px" }}
                      >
                        {data.title}
                      </Typography>
                      {(filterData[data.id] || []).length > 0 && (
                        <Typography
                          sx={{
                            backgroundColor: "#6264a7",
                            color: "white",
                            borderRadius: "12px",
                            padding: "2px 8px",
                            fontSize: "12px",
                            marginRight: "8px",
                          }}
                        >
                          {(filterData[data.id] || []).length}
                        </Typography>
                      )}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{
                          transform: isOpenDropDown
                            ? "rotate(90deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <path
                          d="M8.90961 20.67C8.71961 20.67 8.52961 20.6 8.37961 20.45C8.08961 20.16 8.08961 19.68 8.37961 19.39L14.8996 12.87C15.3796 12.39 15.3796 11.61 14.8996 11.13L8.37961 4.61002C8.08961 4.32002 8.08961 3.84002 8.37961 3.55002C8.66961 3.26002 9.14961 3.26002 9.43961 3.55002L15.9596 10.07C16.4696 10.58 16.7596 11.27 16.7596 12C16.7596 12.73 16.4796 13.42 15.9596 13.93L9.43961 20.45C9.28961 20.59 9.09961 20.67 8.90961 20.67Z"
                          fill="#292D32"
                        />
                      </svg>
                    </Box>

                    {/* Collapsible content */}
                    {isOpenDropDown && (
                      <Box
                        sx={{
                          padding: "8px 16px 16px 16px",
                          backgroundColor: "#fafafa",
                          borderRadius: "0 0 8px 8px",
                          maxHeight: "250px",
                          overflowY: "auto",
                        }}
                      >
                        {/* Search bar */}
                        <input
                          type="search"
                          placeholder={`Search ${data.title}`}
                          style={{
                            width: "100%",
                            padding: "8px 12px",
                            border: "1px solid #d9d9d9",
                            borderRadius: "6px",
                            marginBottom: "12px",
                            fontSize: "14px",
                          }}
                          value={searchText[data.id] || ""}
                          onChange={(e) => {
                            const value = e?.target?.value ?? "";
                            setSearchText((prev) => ({
                              ...prev,
                              [data.id]: value,
                            }));
                          }}
                        />

                        {/* Filter options */}
                        {data.label.length === 0 ? (
                          <Typography
                            sx={{
                              color: "#666",
                              fontSize: "14px",
                              textAlign: "center",
                              padding: "16px",
                            }}
                          >
                            {translation.noDataAvailable}
                          </Typography>
                        ) : options?.length > 0 ? (
                          options.map((label, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                padding: "8px 0",
                                "&:hover": {
                                  backgroundColor: "#f0f0f0",
                                  borderRadius: "4px",
                                  padding: "8px 8px",
                                },
                              }}
                            >
                              <input
                                type="checkbox"
                                id={`${data.id}-${label}`}
                                value={label || ""}
                                checked={(filterData[data.id] || []).includes(
                                  label
                                )}
                                onChange={(e) => {
                                  const checked = e?.target?.checked ?? false;
                                  const value = e?.target?.value ?? label;
                                  toggle(checked, value, data.id);
                                }}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  cursor: "pointer",
                                }}
                              />
                              <label
                                htmlFor={`${data.id}-${label}`}
                                style={{
                                  fontSize: "14px",
                                  cursor: "pointer",
                                  flex: 1,
                                }}
                              >
                                {label}
                              </label>
                            </Box>
                          ))
                        ) : (
                          <Typography
                            sx={{
                              color: "#666",
                              fontSize: "14px",
                              textAlign: "center",
                              padding: "16px",
                            }}
                          >
                            {translation.notFound}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                ) : (
                  // Original tooltip for larger screens
                  <Tooltip
                    title={
                      <div
                        className={`overflow-auto collapse ${
                          isOpenDropDown ? "show" : ""
                        }`}
                        style={{ maxHeight: "300px", overflowY: "auto" }}
                        id={`${data.title}`}
                      >
                        <div className="mx-3 mt-3">
                          <input
                            type="search"
                            id={`search-${data.id}`}
                            name="search"
                            placeholder={`Search ${data.title}`}
                            className="search-box w-100 "
                            value={searchText[data.id] || ""}
                            onChange={(e) => {
                              const value = e?.target?.value ?? "";
                              setSearchText((prev) => ({
                                ...prev,
                                [data.id]: value,
                              }));
                            }}
                          />
                        </div>
                        {data.label.length === 0 ? (
                          <div className="px-5 pt-3 pb-0">
                            <p>{translation.noDataAvailable}</p>
                          </div>
                        ) : options?.length > 0 ? (
                          options.map((label, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: "flex",
                                gap: "10px",
                                marginBottom: "5px",
                                paddingLeft: "6px",
                              }}
                            >
                              <input
                                type="checkbox"
                                id={`${data.id}-${label}`}
                                value={label || ""}
                                checked={(filterData[data.id] || []).includes(
                                  label
                                )}
                                onChange={(e) => {
                                  const checked = e?.target?.checked ?? false;
                                  const value = e?.target?.value ?? label;
                                  toggle(checked, value, data.id);
                                }}
                              />
                              <label
                                htmlFor={`${data.id}-${label}`}
                                className="ms-3"
                              >
                                {label}
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="px-5 pt-3 pb-0">
                            <p>{translation.notFound}</p>
                          </div>
                        )}
                      </div>
                    }
                    placement="left"
                    open={openDropdownIndex === index}
                    arrow
                    disableFocusListener
                    disableHoverListener
                    disableTouchListener
                    classes={{ arrow: classes.arrow, tooltip: classes.tooltip }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        fontFamily: "Karla",
                        width: "96%",
                        margin: "4px",
                        borderRadius: "12px",
                        "&:hover": {
                          cursor: "pointer",
                          background: "#eeeeee",
                        },
                      }}
                    >
                      <Typography
                        onClick={() => handleDropdownToggle(index)}
                        sx={{
                          fontFamily: "Karla",
                          padding: "3px 16px 3px 16px",
                          width: "96%",
                          margin: "4px",
                          borderRadius: "12px",
                          "&:hover": {
                            cursor: "pointer",
                            background: "#eeeeee",
                          },
                          textAlign: "start",
                        }}
                      >
                        {data.title}
                      </Typography>
                      {(filterData[data.id] || []).length > 0 && (
                        <Typography>
                          {(filterData[data.id] || []).length}
                        </Typography>
                      )}
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        style={{ marginRight: "5%" }}
                      >
                        <path
                          d="M8.90961 20.67C8.71961 20.67 8.52961 20.6 8.37961 20.45C8.08961 20.16 8.08961 19.68 8.37961 19.39L14.8996 12.87C15.3796 12.39 15.3796 11.61 14.8996 11.13L8.37961 4.61002C8.08961 4.32002 8.08961 3.84002 8.37961 3.55002C8.66961 3.26002 9.14961 3.26002 9.43961 3.55002L15.9596 10.07C16.4696 10.58 16.7596 11.27 16.7596 12C16.7596 12.73 16.4796 13.42 15.9596 13.93L9.43961 20.45C9.28961 20.59 9.09961 20.67 8.90961 20.67Z"
                          fill="#292D32"
                        />
                      </svg>
                    </Box>
                  </Tooltip>
                )}
              </Box>
            );
          })}
          <div
            style={{
              padding: "10px",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Button
              style={{
                background: "#6264a7",
                color: "white",
                textTransform: "none",
                padding: "0px 28px",
                fontSize: "14px",
                height: "30px",
                fontFamily: "Karla",
              }}
              onClick={applyFilters}
            >
              {translation.apply || "Apply"}
            </Button>
            <Button
              style={{
                textTransform: "none",
                padding: "0px 28px",
                fontSize: "14px",
                height: "30px",
                fontFamily: "Karla",
                border: "transparent",
              }}
              onClick={resetFilters}
            >
              {translation.clear || "Clear"}
            </Button>
          </div>
        </Popover>
      </Modal>

      {selectedGroupId && (
        <GroupNetworkConfigDialog
          open={configDialogOpen}
          onCancel={handleConfigDialogClose}
          onConfirm={handleConfigDialogConfirm}
          shareCollection={config.shareCollection}
          setShareCollection={() => handleConfigChange("shareCollection")}
          shareSpinWheel={config.shareSpinWheel}
          setShareSpinWheel={() => handleConfigChange("shareSpinWheel")}
          note={translation.updateShareNote}
          isUpdating={isUpdatingGroupNetwork?.[selectedGroupId] || false}
        />
      )}

      <Dialog
        open={deleteInviteDialog.open}
        onCancel={() =>
          !deleteInviteDialog.isLoading &&
          setDeleteInviteDialog({
            open: false,
            invitationId: null,
            isLoading: false,
          })
        }
        header={translation.deleteInvitation}
        content={
          deleteInviteDialog.isLoading ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              <Loader />
              <div style={{ marginTop: "10px" }}>
                {translation.deleteInvitationLoading}
              </div>
            </div>
          ) : (
            translation.deleteInvitationConfirm
          )
        }
        confirmButton={{
          content: translation.delete,
          primary: true,
          danger: true,
          disabled: deleteInviteDialog.isLoading,
          onClick: handleDeleteInvite,
        }}
        cancelButton={{
          content: translation.cancel,
          disabled: deleteInviteDialog.isLoading,
        }}
      />

      <ConfirmationDialog
        open={inviteConfirmDialog.open}
        onConfirm={() => handleInviteSchool(inviteConfirmDialog.groupId, inviteConfirmDialog.tenantId)}
        onCancel={() =>
          !inviteConfirmDialog.isLoading &&
          setInviteConfirmDialog({
            open: false,
            groupId: null,
            tenantId: null,
            schoolName: null,
            isLoading: false,
          })
        }
        title={translation.inviteSchool}
        content={`${translation.inviteSchoolConfirm} "${inviteConfirmDialog.schoolName}" ${translation.toJoinGroup}`}
        confirmButton={{
          content: translation.invite,
          primary: true,
          disabled: inviteConfirmDialog.isLoading,
        }}
        cancelButton={translation.cancel}
      />
    </>
  );
};

export default Groups;
